// ─── ORDER / QUOTE WORKBOOK ───────────────────────────────────────────────────
// The spreadsheet twin of invoiceSheet.js. Customers kept asking for their order
// as Excel instead of PDF, and the reason is the work that happens *after* we
// send it: a buyer re-quantifies lines, drops rows, and pastes the rest into
// their own purchasing system. A PDF makes them retype 80+ rows by hand.
//
// WHY EXCELJS, LOADED ON DEMAND
// The browser cannot write .xlsx on its own — the format is a ZIP of XML parts.
// ExcelJS is the only maintained writer that does cell styling, column widths
// and formulas (SheetJS keeps styling behind its paid tier), but its browser
// bundle is ~900 KB. That is far too much to spend on the catalogue's first
// paint for a button most sessions never press, so the import is dynamic and
// the bundle is only fetched on the first click. It resolves to ExcelJS's
// prebuilt browser build via its package "browser" field, so no Node polyfills
// are pulled into the webpack graph.
//
// WHY A REAL .XLSX AND NOT CSV
// We already ship a CSV catalogue export, but CSV cannot carry what a quote
// needs: Arabic survives only via a BOM, every number arrives as text, and
// there is no column width, no RTL and no formula. A quote is a document, not
// a data dump.
//
// VAT IS NEVER APPLIED HERE
// Prices arrive already resolved by the caller's priceOf (App.js getPrice),
// which rounds VAT-inclusive prices up to the nearest 5 EGP. This module only
// LABELS the VAT — recomputing it here would charge it twice.

import { buildThumbMap } from './invoiceSheet.js';
import { saveBlob, XLSX_MIME } from './download.js';

const FOREST = 'FF33402B';   // --sedra-forest
const OLIVE  = 'FF5F6B4A';   // --sedra-olive
const WASH   = 'FFEDEFE5';   // --sedra-olive-wash, the zebra band
const RULE   = 'FFD8D4C4';
const MUTED  = 'FF6B6B5E';
const FONT   = 'Arial';      // ships on Windows/macOS/iOS/Android and shapes Arabic
const THUMB_PX = 44;         // matches the printable sheet's photo cell

const HEADING = {
  quote:   { ar: 'عرض سعر', en: 'PRICE QUOTE' },
  order:   { ar: 'طلب',     en: 'ORDER' },
  invoice: { ar: 'فاتورة',  en: 'INVOICE' },
};

const FOOTNOTE = {
  quote: { ar: 'هذا عرض سعر وليس طلباً مؤكداً.', en: 'This is a price quotation, not a confirmed order.' },
  other: { ar: 'سجل طلب مؤكد.',                  en: 'Record of a confirmed order.' },
};

// Excel has no "reset"; every styled cell needs the full spec, so centralise it.
const paint = (cell, { bold = false, size = 10, color = 'FF000000', fill,
                       align = 'left', wrap = false, rtl = false, fmt: numFmt } = {}) => {
  cell.font = { name: FONT, bold, size, color: { argb: color } };
  cell.alignment = { horizontal: align, vertical: 'middle', wrapText: wrap,
                     ...(rtl ? { readingOrder: 'rtl' } : {}) };
  if (fill)   cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: fill } };
  if (numFmt) cell.numFmt = numFmt;
  return cell;
};

/**
 * Build and download the order/quote as .xlsx.
 * Takes the same shape invoiceSheetHtml does, so both export buttons can be fed
 * from one call site in App.js.
 *
 * @param items  [{ sku, name_en, name_ar, qty, price, variant_label }] — price
 *               already resolved by the caller (null when prices are hidden)
 */
export async function downloadOrderWorkbook({
  lang = 'ar', kind = 'quote', refId, dateMs, party, items = [],
  total, showPrice = true, showVat = false, notes, withPictures = false,
} = {}) {
  if (!items.length) return false;

  // Compress each distinct photo to a small JPEG data URI, exactly as the
  // printable sheet does — a 40-line order embedding full-resolution images
  // would produce a workbook too large to send on WhatsApp. Reuses the shared
  // helper so both exports stay visually identical.
  const [{ default: ExcelJS }, thumbs] = await Promise.all([
    import('exceljs'),
    withPictures ? buildThumbMap(items.map(i => i.image)) : Promise.resolve(null),
  ]);
  // Drop the column entirely if every image failed to load — an empty photo
  // column just wastes width.
  const withPics = !!thumbs && items.some(i => thumbs.get(i.image));

  const isAr = lang === 'ar';
  const head = HEADING[kind] || HEADING.quote;
  const foot = kind === 'quote' ? FOOTNOTE.quote : FOOTNOTE.other;
  // Only spend a column on variants when something actually carries one.
  const withVariant = items.some(i => i.variant_label);

  const wb = new ExcelJS.Workbook();
  wb.creator = 'Sedra Home';
  wb.created = new Date(dateMs || Date.now());
  const ws = wb.addWorksheet(head.en, {
    views: [{ showGridLines: false }],
    pageSetup: { orientation: 'landscape', fitToPage: true, fitToWidth: 1, fitToHeight: 0 },
  });

  const cols = [
    { key: 'idx',  header: '#',                       width: 5  },
    ...(withPics ? [{ key: 'img', header: isAr ? 'صورة' : 'Image', width: 10 }] : []),
    { key: 'sku',  header: 'SKU',                     width: 14 },
    { key: 'en',   header: isAr ? 'المنتج / Product' : 'Product', width: 46 },
    { key: 'ar',   header: 'الوصف بالعربية',           width: 46 },
    ...(withVariant ? [{ key: 'var', header: isAr ? 'الخيار' : 'Variant', width: 18 }] : []),
    { key: 'qty',  header: isAr ? 'الكمية' : 'Qty',    width: 8  },
    ...(showPrice ? [
      { key: 'unit', header: isAr ? 'سعر الوحدة (EGP)' : 'Unit Price (EGP)', width: 16 },
      { key: 'sub',  header: isAr ? 'المجموع (EGP)'    : 'Subtotal (EGP)',   width: 17 },
    ] : []),
  ];
  ws.columns = cols.map(c => ({ key: c.key, width: c.width }));
  const at = key => ws.getColumn(cols.findIndex(c => c.key === key) + 1).letter;
  const LAST = cols.length;

  // ── title + meta ───────────────────────────────────────────────────────────
  ws.mergeCells(1, 1, 1, LAST);
  paint(ws.getCell('A1'), { bold: true, size: 15, color: 'FFFFFFFF', fill: FOREST, align: 'center' })
    .value = `Sedra Home  —  ${head.en}   |   سدرة هوم — ${head.ar}`;
  ws.getRow(1).height = 30;

  const dateStr = new Date(dateMs || Date.now())
    .toLocaleDateString(isAr ? 'ar-EG' : 'en-GB', { year: 'numeric', month: 'long', day: 'numeric' });
  const meta = [
    [isAr ? 'رقم المستند' : 'Reference', refId || ''],
    [isAr ? 'التاريخ'     : 'Date',      dateStr],
    ...(party ? [
      [isAr ? 'العميل'    : 'Customer',  party.company_name || ''],
      [isAr ? 'جهة الاتصال' : 'Contact',
       [party.contact_name, party.phone].filter(Boolean).join('  ·  ')],
    ] : []),
  ];
  meta.forEach(([k, v], i) => {
    const r = 2 + i;
    paint(ws.getCell(r, 1), { bold: true, size: 9, color: OLIVE }).value = k;
    ws.mergeCells(r, 2, r, LAST);
    paint(ws.getCell(r, 2), { size: 10 }).value = v;
  });

  // ── header row ─────────────────────────────────────────────────────────────
  const HEAD = 2 + meta.length + 1;
  cols.forEach((c, i) => {
    paint(ws.getCell(HEAD, i + 1), {
      bold: true, size: 10, color: 'FFFFFFFF', fill: OLIVE,
      align: 'center', wrap: true, rtl: c.key === 'ar',
    }).value = c.header;
  });
  ws.getRow(HEAD).height = 26;

  // ── line items ─────────────────────────────────────────────────────────────
  const FIRST = HEAD + 1;
  items.forEach((item, n) => {
    const r = FIRST + n;
    const row = ws.getRow(r);
    const band = n % 2 ? WASH : undefined;
    const unit = item.price == null ? null : Number(item.price);
    const put = (key, value, opts) => {
      const cell = ws.getCell(r, cols.findIndex(c => c.key === key) + 1);
      paint(cell, { size: 9, fill: band, ...opts }).value = value;
      return cell;
    };
    put('idx', n + 1,                 { align: 'center' });
    if (withPics) {
      put('img', '', {});
      const thumb = thumbs.get(item.image);
      // Only data URIs can be embedded. A tainted canvas falls back to the
      // remote URL, which the workbook cannot inline — leave that cell blank
      // rather than shipping a broken picture.
      const m = /^data:image\/(png|jpeg);base64,(.+)$/.exec(thumb || '');
      if (m) {
        ws.addImage(
          wb.addImage({ base64: m[2], extension: m[1] }),
          { // fractional offsets inset the photo so it does not touch the rules
            tl: { col: cols.findIndex(c => c.key === 'img') + 0.12, row: r - 1 + 0.1 },
            ext: { width: THUMB_PX, height: THUMB_PX },
            editAs: 'oneCell',
          },
        );
      }
    }
    put('sku', item.sku || '',        { align: 'left' });
    put('en',  item.name_en || '',    { wrap: true });
    put('ar',  item.name_ar || '',    { wrap: true, align: 'right', rtl: true });
    if (withVariant) put('var', item.variant_label || '', { wrap: true });
    put('qty', item.qty,              { align: 'center', fmt: '#,##0' });
    if (showPrice) {
      put('unit', unit,               { align: 'right', fmt: '#,##0' });
      // A live formula, not a frozen number: the whole reason buyers want Excel
      // is to adjust quantities and watch the total follow. `result` caches the
      // value so previewers that never recalculate still show a real figure.
      put('sub', unit == null ? null : {
        formula: `${at('qty')}${r}*${at('unit')}${r}`,
        result: unit * item.qty,
      }, { align: 'right', fmt: '#,##0' });
    }
    for (let c = 1; c <= LAST; c++) {
      ws.getCell(r, c).border = { bottom: { style: 'thin', color: { argb: RULE } } };
    }
    // Rows must clear the photo, or Excel crops it to the row box.
    row.height = withPics ? THUMB_PX * 0.78 : 26;
  });
  const END = FIRST + items.length - 1;

  // ── total ──────────────────────────────────────────────────────────────────
  let cursor = END + 1;
  if (showPrice && total != null) {
    const vatNote = showVat
      ? (isAr ? '  (شامل ضريبة القيمة المضافة ١٤٪)' : '  (includes 14% VAT)')
      : '';
    ws.mergeCells(cursor, 1, cursor, LAST - 1);
    paint(ws.getCell(cursor, 1), {
      bold: true, size: 11, color: 'FFFFFFFF', fill: FOREST, align: 'right',
    }).value = `${isAr ? 'الإجمالي' : 'TOTAL'}${vatNote}`;
    paint(ws.getCell(cursor, LAST), {
      bold: true, size: 12, color: 'FFFFFFFF', fill: FOREST, align: 'right', fmt: '#,##0',
    }).value = { formula: `SUM(${at('sub')}${FIRST}:${at('sub')}${END})`, result: Number(total) };
    ws.getRow(cursor).height = 28;
    cursor += 1;
  }

  // ── notes ──────────────────────────────────────────────────────────────────
  cursor += 1;
  const line = (text) => {
    ws.mergeCells(cursor, 1, cursor, LAST);
    paint(ws.getCell(cursor, 1), { size: 9, color: MUTED, align: 'center' }).value = text;
    cursor += 1;
  };
  if (notes) line(notes);
  line(`${foot.en}  ·  ${foot.ar}`);
  if (showPrice) {
    line(isAr
      ? 'المجموع يُحتسب تلقائيًا (الكمية × سعر الوحدة) — عدّل الكمية ليتحدث الإجمالي.'
      : 'Subtotal is a live formula (Qty × Unit Price) — edit a quantity and the total follows.');
  }

  ws.views = [{ state: 'frozen', ySplit: HEAD, showGridLines: false }];
  ws.autoFilter = { from: { row: HEAD, column: 1 }, to: { row: END, column: LAST } };
  // Repeat the header on every printed page.
  ws.pageSetup.printTitlesRow = `${HEAD}:${HEAD}`;

  const buf = await wb.xlsx.writeBuffer();
  saveBlob(new Blob([buf], { type: XLSX_MIME }),
           `${head.en} — ${refId || Date.now()}.xlsx`);
  return true;
}
